import { Box } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { useTranslation } from "react-i18next"

import {
  BatchUpdateKeysEnum,
  BatchUpdateKeysType,
} from "@tfg/types/schemas/modules/traceability/dtos/find-empty-batch-operations"

import { useBatchUpdateModalContext } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/BatchModal/store/BatchModalContext.tsx"
import { BatchPopover } from "~/pages/SupplierPortal/TraceabilityRequests/components/BatchPopover.tsx"
import { useTraceabilityOperations } from "~/pages/SupplierPortal/TraceabilityRequests/context/TraceabilityOperationsContext"
import { useBatchPopoverTarget } from "~/pages/SupplierPortal/TraceabilityRequests/hooks/useBatchPopoverTarget"

export enum BatchPopoverOperationItemType {
  contact = "contact",
  productionSite = "productionSite",
  reference = "reference",
  purchaseOrderRef = "purchaseOrderRef",
}

export enum BatchPopoverOperationFieldName {
  contact = "contactId",
  productionSite = "productionSiteId",
  reference = "reference",
  purchaseOrderRef = "purchaseOrderRef",
}

export type BatchPopoverOperationTypeToField = {
  [K in BatchPopoverOperationItemType]: BatchUpdateKeysType
}

export const operationTypeToFieldMapping: BatchPopoverOperationTypeToField = {
  [BatchPopoverOperationItemType.contact]: BatchUpdateKeysEnum.enum.contactId,
  [BatchPopoverOperationItemType.productionSite]:
    BatchUpdateKeysEnum.enum.productionSiteId,
  [BatchPopoverOperationItemType.reference]: BatchUpdateKeysEnum.enum.reference,
  [BatchPopoverOperationItemType.purchaseOrderRef]:
    BatchUpdateKeysEnum.enum.purchaseOrderRef,
}

type Props<T> = {
  onChange: (value: T) => void
  value: T
  type: BatchPopoverOperationItemType
}

export const BatchPopoverOperationItem = <T,>({
  onChange,
  value,
  type,
}: Props<T>) => {
  const { t } = useTranslation("traceability")
  const { operationItem } = useTraceabilityOperations()
  const { setBatchModalInfo, setBatchModalOpen } = useBatchUpdateModalContext()

  const [opened, { close, open }] = useDisclosure(false)
  const target = useBatchPopoverTarget(type, value, onChange, open)

  const labels = t("batchModal.labels", {
    returnObjects: true,
  })
  const openBatchModal = (type: BatchPopoverOperationItemType) => {
    if (!operationItem.operation) {
      return
    }
    setBatchModalInfo({
      activityId: operationItem.activityId,
      operationId: operationItem.operation?.id,
      requestId: operationItem.operation?.informationRequestId,
      update: {
        contactId: operationItem.operation.contact.id,
        [operationTypeToFieldMapping[type]]: value,
        doNotKnow: operationItem.operation?.doNotKnow ?? false,
      },
      label: labels[type],
      key: operationTypeToFieldMapping[type],
    })
    setBatchModalOpen(true)
  }

  return (
    <BatchPopover
      target={<Box>{target}</Box>}
      close={close}
      onClick={() => {
        close()
        openBatchModal(type)
      }}
      opened={opened}
      type={type}
    />
  )
}
